import { Button } from '@shared/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shared/components/common/Form';
import { Input, PasswordInput } from '@shared/components/common/Input';
import { useStore } from '@shared/hooks/useStore';
import yup from '@shared/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getTitlePage } from '@shared/utils/routes';
import { REGEX_VALIDATE_EMAIL, SYSTEM } from '@shared/components/constants/common.constants';
import { ROUTES, SCREEN_TYPE } from '@shared/configs/constants';

export default observer(function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const callbackURL = searchParams.get('redirect');
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            if (callbackURL) {
                window.location.href = decodeURIComponent(callbackURL);
            }
            navigate(ROUTES.home.href);
        }
    }, []);
    // hooks

    // store
    const {
        authStore: { login },
        modalStore: { hideAllModals }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .matches(REGEX_VALIDATE_EMAIL, 'メールアドレスが正しくありません。')
            .required('必須項目をご入力ください。'),
        password: yup.string().required('必須項目をご入力ください。'),
        rememberMe: yup.boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: ''
        }
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const { email, password } = data;
        const res = await flowResult(login({ email, password, screenType: SCREEN_TYPE.GU }));
        if (res) {
            if (callbackURL) {
                window.location.href = decodeURIComponent(callbackURL);
                return;
            }
            window.location.href = ROUTES.home.href;
        }
    };

    const showLogin = () => {
        hideAllModals();
        navigate(`${ROUTES.registrationStepOne.href}?sub=1`);
    };

    const showRegister = () => {
        hideAllModals();
        navigate(ROUTES.forgotPassword.href);
    };

    return (
        <div className='sm:bg-background-2 bg-[#F1F1F1] flex justify-center items-center lg:py-[30px] md:py-[24px] sm:rounded-xl'>
            <div className='mx-auto flex w-full flex-col justify-center sm:w-[640px] rounded-[14px] md:gap-8 gap-6 p-8'>
                <div className='text-center page-title-hero text-[#121212]'>ログイン</div>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6 md:space-y-8'>
                        <div className='space-y-4 sm:space-y-6'>
                            <FormField
                                control={form.control}
                                name='email'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className='text-[16px] text-[#121212]' required>メールアドレス</FormLabel>
                                        <FormControl>
                                            <Input {...field} className='h-[56px] rounded-none bg-[#ffffff]' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='password'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className='text-[16px] text-[#121212] ' required>パスワード</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showPassword ? 'password' : 'text'}
                                                onToggleIcon={() => setShowPassword(!showPassword)}
                                                {...field}
                                                className='h-[56px] rounded-none bg-[#ffffff]'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <Button type='submit' variant='default' className='w-full rounded-[10px] h-[56px] text-[18px] font-bold text-[#ffffff]'>
                            送信
                        </Button>

                        <div className='h-[1px] w-full bg-[#D9D9D9]'></div>

                        <div className='space-y-4 sm:space-y-6'>
                            <p className='text-center'>
                                <button
                                    type='button'
                                    onClick={showRegister}
                                    className='underline text-[#016FD0] hover:no-underline text-sm md:text-base'
                                >
                                    パスワードを忘れた方はこちら
                                </button>
                            </p>
                            <p className='text-center'>
                                <button
                                    type='button'
                                    onClick={showLogin}
                                    className='underline text-[#016FD0] hover:no-underline text-sm md:text-base'
                                >
                                    新規登録がお済みでない方はこちら
                                </button>
                            </p>
                        </div>
                    </form>

                </Form>
            </div>
        </div>

    );
});
